import * as React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useLocation } from 'react-router-dom';

import placementCancelReasons from '@careerstart/wae-common/schema/models/placement/enums/placementCancelReasons';
import { PLACEMENT_STATUS_CANCELLED } from '@careerstart/wae-common/src/main/constants/constants';
import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import CancelIcon from '@mui/icons-material/Cancel';
import { Box, FormControlLabel, Grid, Switch, Typography } from '@mui/material';

import TimezoneBadge from '../../../../../assets/icons/TimezoneBadge';
import TextLink from '../../../../../components/TextLink/TextLink';
import selectUser from '../../../../../store/selectors/appSelector';
import { selectReliabilityExclusionPlacementId } from '../../../../../store/selectors/userListSelector';
import { BLACK, PRIMARY_COLOR, RED } from '../../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../../theme/fontConstants';
import {
  epochToDateInReadableFormat,
  epochToDayOfWeek,
  epochToTimeInReadableFormat,
  epochToTimezoneAbbr,
} from '../../../../../utils';
import { ROUTE_CONSTANTS } from '../../../../app/RouteBuilding/routeConstants';
import { setReliabilityExclusion } from '../../../usersListReducer';

const Title = ({ text }) => (
  <Typography
    sx={{
      fontSize: '12px',
      color: BLACK[70],
      textTransform: 'uppercase',
      fontFamily: PRIMARY_FONT[300],
    }}
  >
    {text}
  </Typography>
);

Title.propTypes = {
  text: PropTypes.string,
};

const RowCards = ({ placements = [] }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const sessionUser = useSelector(selectUser);
  const reliabilityExclusionPlacementId = useSelector(selectReliabilityExclusionPlacementId);
  const isAdmin = sessionUser?.role === UserRole.ADMIN;
  const excludeFromReliabilityLabel = LanguageConverterV2(
    'userDetail.workerPlacements.excludeFromReliability'
  );

  return placements.length > 0 ? (
    placements?.map((placement) => {
      const isCancelled = placement?.state?.status === PLACEMENT_STATUS_CANCELLED;
      const isWorkerCancellation =
        placement?.state?.reason === placementCancelReasons.candidateCancelled;

      return (
        <Grid
          container
          sx={{
            border: `1px solid ${BLACK[40]}`,
            borderRadius: '5px',
          }}
          key={placement?._id}
        >
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{
              minHeight: '43px',
              backgroundColor: isCancelled ? RED[10] : BLACK[10],
              borderTopLeftRadius: '5px',
              borderTopRightRadius: '5px',
              padding: '0 12px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  fontSize: '15px',
                  fontFamily: PRIMARY_FONT[500],
                }}
              >
                {epochToDayOfWeek(placement.start)}
              </Typography>
              <Typography
                sx={{
                  fontSize: '15px',
                  fontFamily: PRIMARY_FONT[400],
                  paddingLeft: '12px',
                }}
              >
                {epochToDateInReadableFormat(placement.start)}
              </Typography>
            </Box>

            {isCancelled && !isWorkerCancellation && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography
                  sx={{ fontSize: '14px', fontFamily: PRIMARY_FONT[500], color: RED[50] }}
                >
                  {`${LanguageConverterV2('userDetail.workerPlacements.cancelledBy')} `}
                  <TextLink
                    to={
                      placement?.state?.user
                        ? `${generatePath(`/${ROUTE_CONSTANTS.USERS_V2}/:id`, {
                            id: placement.state.user,
                          })}${location.search}`
                        : undefined
                    }
                    sx={{
                      fontSize: '14px',
                      fontFamily: PRIMARY_FONT[500],
                      color: RED[50],
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    {placement?.state?.userName}
                  </TextLink>
                </Typography>
                <Typography
                  sx={{ fontSize: '13px', fontFamily: PRIMARY_FONT[400], color: RED[50] }}
                >
                  {LanguageConverterV2('userDetail.workerPlacements.cancelledOnDateAtTime', {
                    date: epochToDateInReadableFormat(placement?.state?.timestamp),
                    time: epochToTimeInReadableFormat(placement?.state?.timestamp),
                  })}
                </Typography>
              </Box>
            )}

            {isCancelled && isWorkerCancellation && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <CancelIcon sx={{ fontSize: '18px', color: RED[70] }} />
                  <Typography
                    sx={{ fontSize: '14px', fontFamily: PRIMARY_FONT[500], color: RED[50] }}
                  >
                    {LanguageConverterV2('userDetail.workerPlacements.workerCancellation')}
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: '13px', fontFamily: PRIMARY_FONT[400], color: RED[50], pl: 2 }}
                >
                  {LanguageConverterV2('userDetail.workerPlacements.cancelledOnDateAtTime', {
                    date: epochToDateInReadableFormat(placement?.state?.timestamp),
                    time: epochToTimeInReadableFormat(placement?.state?.timestamp),
                  })}
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid container direction="row" flexWrap="nowrap" sx={{ padding: '12px' }}>
            <Grid container direction="row" flexWrap="nowrap">
              <Grid container direction="column">
                <Title text={LanguageConverterV2('userDetail.workerPlacements.company')} />
                <Typography
                  data-testid="company-name"
                  sx={{ fontSize: '16px', fontFamily: PRIMARY_FONT[300] }}
                >
                  {placement?.corporation?.name}
                </Typography>
              </Grid>
            </Grid>

            <Grid container direction="column">
              <Title text={LanguageConverterV2('userDetail.workerPlacements.position')} />
              <TextLink
                data-testid="position-name"
                to={
                  placement?.jobOrder?._id
                    ? `${generatePath(`/${ROUTE_CONSTANTS.JOB_ORDERS}/:id`, {
                        id: placement.jobOrder._id,
                      })}${location.search}`
                    : undefined
                }
                sx={{
                  fontSize: '16px',
                  fontFamily: PRIMARY_FONT[300],
                  color: PRIMARY_COLOR[70],
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                {placement.jobOrder.name}
              </TextLink>
            </Grid>

            <Grid container direction="column">
              <Title text={LanguageConverterV2('userDetail.workerPlacements.times')} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  data-testid="time-range"
                  sx={{ fontSize: '16px', fontFamily: PRIMARY_FONT[300] }}
                >
                  {`${epochToTimeInReadableFormat(placement.start)}-${epochToTimeInReadableFormat(
                    placement.end
                  )}`}
                </Typography>
                <TimezoneBadge timezone={epochToTimezoneAbbr(placement.end)} />
              </Box>
            </Grid>
            <Grid container direction="row" flexWrap="nowrap">
              <Grid container direction="column">
                <Title text={LanguageConverterV2('userDetail.workerPlacements.cancelNote')} />
                <Typography
                  data-testid="cancellation-reason"
                  sx={{ fontSize: '16px', fontFamily: PRIMARY_FONT[300] }}
                >
                  {placement?.state?.cancelNote || '--'}
                </Typography>
              </Grid>
            </Grid>
            {isAdmin && (
              <Grid container direction="column" sx={{ flexShrink: 0, maxWidth: 200 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={placement?.flags?.isExcludedFromReliability === true}
                      disabled={
                        String(reliabilityExclusionPlacementId || '') === String(placement?._id)
                      }
                      onChange={(event) => {
                        dispatch(
                          setReliabilityExclusion({
                            placementId: placement._id,
                            isExcludedFromReliability: event.target.checked,
                          })
                        );
                      }}
                      inputProps={{
                        'aria-label': excludeFromReliabilityLabel,
                        'data-testid': 'exclude-from-reliability',
                      }}
                    />
                  }
                  label={excludeFromReliabilityLabel}
                  sx={{
                    m: 0,
                    alignItems: 'flex-start',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '13px',
                      fontFamily: PRIMARY_FONT[400],
                      lineHeight: 1.3,
                    },
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      );
    })
  ) : (
    <Grid
      container
      justifyContent="center"
      sx={{
        backgroundColor: BLACK[10],
        borderRadius: '5px',
        height: '40px',
      }}
    />
  );
};
RowCards.propTypes = {
  placements: PropTypes.arrayOf(PropTypes.shape({})),
};

export default RowCards;
